import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";

import "../../Style/floatingLang.css";

const LANGUAGES = [
  {
    code: "en",
    label: "EN",
    fullName: "English",
    flag: "https://flagcdn.com/w40/us.png",
  },
  {
    code: "fr",
    label: "FR",
    fullName: "Français",
    flag: "https://flagcdn.com/w40/fr.png",
  },
];

export default function FloatingMobileLang() {
  const [currentLang, setCurrentLang] = useState("en");
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef(null);

  // Read existing translation cookie
  useEffect(() => {
    const match = document.cookie.match(/(^|;) ?googtrans=([^;]*)(;|$)/);
    if (match) {
      const code = match[2].split("/").pop();
      if (code) setCurrentLang(code);
    }
  }, []);

  // 5-second auto-close timer
  useEffect(() => {
    if (isOpen) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 5000);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelectLanguage = (langCode) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (langCode === currentLang) {
      setIsOpen(false);
      return;
    }

    setCurrentLang(langCode);
    setIsOpen(false);

    if (langCode === "en") {
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    } else {
      const cookieVal = `/en/${langCode}`;
      document.cookie = `googtrans=${cookieVal}; path=/;`;
      document.cookie = `googtrans=${cookieVal}; path=/; domain=${window.location.hostname};`;
    }

    window.location.reload();
  };

  const active = LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0];

  return (
    <div className="floating-lang notranslate">
      {/* Floating edge trigger tab */}
      <button
        type="button"
        className={`floating-lang__tab ${isOpen ? "is-active" : ""}`}
        onClick={handleToggle}
        aria-label="Toggle language menu"
      >
        <img
          src={active.flag}
          alt={active.label}
          className="floating-lang__flag"
          width="16"
          height="11"
        />
        <span className="floating-lang__code">{active.label}</span>
        <ChevronRight
          size={12}
          className={`floating-lang__arrow ${isOpen ? "is-open" : ""}`}
        />
      </button>

      {/* Flyout selector drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="floating-lang__panel"
            initial={{ opacity: 0, x: -16, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -12, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onMouseEnter={() => {
              // Pause timer if user hovers/touches
              if (timerRef.current) clearTimeout(timerRef.current);
            }}
            onMouseLeave={() => {
              // Restart 5s timer
              timerRef.current = setTimeout(() => setIsOpen(false), 5000);
            }}
          >
            {LANGUAGES.map((lang) => {
              const isSelected = lang.code === currentLang;
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`floating-lang__option ${isSelected ? "is-selected" : ""}`}
                  onClick={() => handleSelectLanguage(lang.code)}
                >
                  <img
                    src={lang.flag}
                    alt={lang.label}
                    className="floating-lang__flag"
                    width="16"
                    height="11"
                  />
                  <span>{lang.fullName}</span>
                  {isSelected && <Check size={14} className="floating-lang__check" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}